import type {
  IMemberCountry,
  IRegion,
  ITimeZone,
} from '@/assets/types/api/Common';

enum EnumRegionErrorType {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
}

enum EnumLoginErrorType {
  E = 'E',
  F = 'F',
}

interface IEmployeeRange {
  id: number;
  description: string;
}

interface IRegionData {
  isLogin: boolean;
  regionCode: EnumRegion | null;
  errorType: EnumRegionErrorType | null;
}

interface ILoginState {
  isFailed: boolean;
  errorType: EnumLoginErrorType | null;
}

export const useMainStore = defineStore('mainStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  // 從 server side 取得的使用者 IP
  const serverIp = ref<string>('');
  // 從 client side 取得的使用者 IP
  const clientIp = ref<string>('');

  const regions = ref<IRegion[]>([]);
  const regionData = ref<IRegionData>({
    isLogin: false,
    regionCode: null,
    errorType: null,
  });
  const loginState = ref<ILoginState>({
    isFailed: false,
    errorType: null,
  });
  const browserLocale = ref<string>('');
  const memberCountries = ref<IMemberCountry[]>([]);
  const countries = ref<string[]>([]);
  const timeZone = ref<ITimeZone[]>([]);
  const isInitialized = ref<boolean>(false);
  const isAppLoading = ref<boolean>(false);
  const isBodyFixed = ref<boolean>(false);
  const isLiveChatReady = ref<boolean>(false);
  const isShowFormSuccessPopUp = ref<boolean>(false);
  const isShowFormWarningPopUp = ref<boolean>(false);

  // 前一頁的路由名稱
  const previousPathName = ref<string>('');

  /* Getters */

  const regionCode = computed(
    (): EnumRegion | null => regionData.value.regionCode
  );

  const matchedLocale = computed((): EnumLocale => {
    // 根據 regionCode 和 regions 取得對應的 locale
    const { $i18n } = nuxtApp;
    const { locale } = $i18n;

    if (regionCode.value) {
      const regionItem = regions.value.find(
        (item) => item.regionCode === regionCode.value
      );

      if (regionItem) {
        const defaultItem = regionItem.websites.find((item) => item.isDefault);

        const browserItem = regionItem.websites.find(
          (item) => item.language === browserLocale.value
        );

        if (browserItem) {
          return browserItem.code;
        } else if (defaultItem) {
          return defaultItem.code;
        } else {
          return EnumLocale.EN_INT;
        }
      }
    }

    const lang = browserLocale.value.toLowerCase().slice(0, 2);
    const isCN = locale.value === EnumLocale.ZH_CN;

    switch (lang) {
      case 'zh':
        return isCN ? EnumLocale.ZH_CN : EnumLocale.ZH_TW;
      case 'ja':
        return EnumLocale.JA_JP;
      case 'ko':
        return EnumLocale.EN_KR;
      default:
        return EnumLocale.EN_INT;
    }
  });

  const employeeRanges = computed((): IEmployeeRange[] => {
    // 員工人數
    return [
      { id: 1, description: '1-20' },
      { id: 2, description: '21-200' },
      { id: 3, description: '201-10000' },
      { id: 4, description: '10000+' },
    ];
  });

  /* Actions */

  const USE_GET_COUNTRIES = async () => {
    /**
     * 取得國家清單（Client Only）
     */
    const { $notification } = nuxtApp;

    return await useCustomFetch(() => '/marketplace/api/v1/common/countries', {
      method: 'GET',
      server: false,
      lazy: true,
      immediate: true,
      onResponse: ({ response }) => {
        const { _data, status } = response;

        if (status >= 200 && status < 300) {
          countries.value = [...(_data as string[])];
        } else {
          console.error(_data);
          $notification({
            message: (_data as { message: string }).message,
            type: 'error',
          });
        }
      },
    });
  };

  const USE_GET_MEMBER_COUNTRIES = async () => {
    /**
     * 取得電話國家區碼清單（Client Only）
     */
    const { $notification } = nuxtApp;

    return await useCustomFetch(
      () => '/marketplace/api/v1/common/phone-country-codes',
      {
        method: 'GET',
        server: false,
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            memberCountries.value = [...(_data as IMemberCountry[])];
          } else {
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const USE_GET_TIME_ZONES = async () => {
    /**
     * 取得時間時區選單
     */
    const { $notification } = nuxtApp;

    return await useCustomFetch(() => '/marketplace/api/v1/common/time-zones', {
      method: 'GET',
      lazy: true,
      immediate: true,
      onResponse: ({ response }) => {
        const { _data, status } = response;

        if (status >= 200 && status < 300) {
          timeZone.value = [...(_data as ITimeZone[])];
        } else {
          console.error(_data);
          $notification({
            message: (_data as { message: string }).message,
            type: 'error',
          });
        }
      },
    });
  };

  const USE_GET_CLIENT_IP = async () => {
    /**
     * 取得使用者的 IP 位址（在 Client Side 透過 API 取得）
     */

    return useFetch(
      'https://apis-corp.advantech.com/api/v1/utility/http/client-ip',
      {
        method: 'GET',
        server: false,
        immediate: false,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            clientIp.value = (_data as { ip?: string }).ip || '';
          } else {
            console.log(_data);
          }
        },
      }
    );
  };

  const GET_REGIONS = async (): Promise<void> => {
    /**
     * 取得區域清單
     */
    const { $customFetch, $notification } = nuxtApp;

    if (regions.value.length > 0) {
      return;
    }

    try {
      const data = await $customFetch<IRegion[]>(
        '/marketplace/api/v1/common/regions',
        { method: 'GET' }
      );
      regions.value = data;
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
    }
  };

  const GET_CHECK_LOGIN = async (): Promise<void> => {
    /**
     * 取得使用者登入狀態
     */
    const { $customFetch, $notification } = nuxtApp;

    interface IResponse {
      isLogin: boolean;
      regionCode: EnumRegion | null;
    }

    try {
      const data = await $customFetch<IResponse>(
        `/marketplace/api/v1/user/check-login?ip=${serverIp.value}`,
        { method: 'GET' }
      );
      const { isLogin, regionCode } = data;
      regionData.value.isLogin = isLogin;
      regionData.value.regionCode = regionCode;
    } catch (error) {
      $notification({
        message: (error as { message: string }).message,
        type: 'error',
      });
    }
  };

  return {
    serverIp,
    clientIp,
    regions,
    regionData,
    loginState,
    browserLocale,
    memberCountries,
    countries,
    isInitialized,
    isAppLoading,
    isBodyFixed,
    isLiveChatReady,
    timeZone,
    isShowFormSuccessPopUp,
    isShowFormWarningPopUp,
    previousPathName,
    regionCode,
    matchedLocale,
    employeeRanges,
    USE_GET_COUNTRIES,
    USE_GET_MEMBER_COUNTRIES,
    USE_GET_TIME_ZONES,
    USE_GET_CLIENT_IP,
    GET_REGIONS,
    GET_CHECK_LOGIN,
  };
});

export { EnumLoginErrorType, EnumRegionErrorType };
